import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { TemplateBuilder } from './components/TemplateBuilder';
import { useDocumentTemplates } from '../../core/hooks/useDocumentTemplates';
import { useAuth } from '../../core/contexts/AuthContext';
import type { DocumentTemplateType } from '../../core/models/Document';
import { FileText, ArrowLeft } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../core/services/firebase';

export const TemplateEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { organizationId } = useAuth();
  const { updateTemplate } = useDocumentTemplates(organizationId);
  const [template, setTemplate] = useState<Partial<DocumentTemplateType> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (!templateId) {
        setError(new Error('Template ID is required'));
        setLoading(false);
        return;
      }

      try {
        const templateRef = doc(db, 'templates', templateId);
        const templateSnap = await getDoc(templateRef);

        if (templateSnap.exists()) {
          const data = templateSnap.data();
          setTemplate({
            id: templateSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as DocumentTemplateType);
        } else {
          setError(new Error('Template not found'));
        }
      } catch (err) {
        console.error('Error fetching template:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [templateId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!template || !templateId) {
      return;
    }

    if (!template.name || !template.organizationId) {
      return;
    }

    if (!template.content || template.content.trim() === '') {
      alert('HTML content is required. Please enter the template HTML.');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateTemplate(templateId, {
        name: template.name,
        description: template.description,
        templatePath: template.templatePath || '',
        content: template.content || '', // Store HTML content
        dynamicFields: template.dynamicFields || [],
        isActive: template.isActive ?? true,
      });
      navigate('/documents/templates');
    } catch (error) {
      console.error('Error updating template:', error);
      alert('Failed to update template. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading template..." />;
  }

  if (error || !template) {
    return <ErrorState message={error?.message || 'Template not found'} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit Document Template"
        description="Update template fields and settings"
        icon={FileText}
        actions={
          <Button variant="outline" onClick={() => navigate('/documents/templates')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Templates
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <TemplateBuilder template={template} onChange={setTemplate} />

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/documents/templates')}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Update Template
          </Button>
        </div>
      </form>
    </div>
  );
};
